import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Episode, Movie, MovieDetails } from 'types/Asset';
import { PurchaseOptionConfirmation } from 'components/PurchaseOptionConfirmation/PurchaseOptionConfirmation';
import { Offer, RentData, Service, SubscribeOption } from 'types/Entitlement';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import GSApi from 'api/Api';
import { setPurchaseSynced } from 'providers/reducers/purchase-synced-reducer';
import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import translate from 'utils/fnTranslate';
import { FetchParamRent, PinType } from 'types/ApiTypes';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { PinFlowType, usePin } from 'providers/usePin/PinContext';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { UpsellOptionSelector } from 'components/UpsellOptionSelector/UpsellOptionSelector';
import { getOfferTypeByTag, getPurchaseErrorCode, getPurchaseErrorTranslationKeys } from 'utils/fnEntitlement';
import { ServiceOptionCover } from 'types/Cover';
import { isEpisode, isRentable } from 'utils/fnTypeGuards';
import { useSubscription } from 'providers/useSubscription/SubscriptionContext';
import { SubscriptionConfirmation } from 'components/SubscriptionConfirmation/SubscriptionConfirmation';
import { Rentable } from 'types/CommonTypes';
import { useTransactionSuccessNotification } from 'components/Notification/Notification';
import { useVODPurchases } from 'providers/useVODPurchases/useVODPurchases';
import { usePurchase } from 'providers/usePurchase/PurchaseContext';
import { useApp } from 'providers/useApp/AppContext';
import { useNavigate } from 'react-router';

export enum UpsellFlowStep {
    SELECT_OFFER = 'select_offer',
    CONFIRM_PURCHASE_OFFER = 'confirm_purchase_offer',
    CONFIRM_SUBSCRIPTION_OFFER = 'confirm_subscription_offer',
    ENTER_PIN = 'enter_pin',
    ERROR = 'upsell_error',
}

/**
 * UpsellFlowContext handles the purchase/renting flow when a VOD asset may have multiple options.
 * Fetches the services API to determine available options and routes to the appropriate flow:
 * - Both TVOD (rental) AND services available -> Shows UpsellOptionSelector with both options
 * - Only TVOD available (services empty) -> Redirects to PurchaseContext for rental
 * - Only services available (no TVOD) -> Redirects to SubscriptionContext for subscription
 */

const useUpsellFlowService = () => {
    const [asset, setAsset] = useState<Episode | Movie | MovieDetails>(null);
    const [step, setStep] = useState<UpsellFlowStep>(null);
    const [vodTermId, setVodTermId] = useState(null);
    const [checkingServices, setCheckingServices] = useState(false);
    const [playPath, setPlayPath] = useState<string>(null);

    const [selectedOffer, setSelectedOffer] = useState<Rentable | SubscribeOption>(null);
    const [selectedRentOffer, setSelectedRentOffer] = useState<RentData>(null);
    const [pendingSubscriptionOffer, setPendingSubscriptionOffer] = useState<SubscribeOption>(null);
    const [cachedServicesResponse, setCachedServicesResponse] = useState<any>(null);
    const [purchaseErrorCode, setPurchaseErrorCode] = useState<string>(null);

    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { appLanguage } = useApp();
    const { openPinDialog, closePinDialog, validatedPurchasePin } = usePin();
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const confirmTransactionSuccess = useTransactionSuccessNotification();

    const { initPurchaseFlow } = usePurchase();

    const { purchaseSubscription, initSubscribeFlow } = useSubscription();

    const {
        error: rentError,
        response: rentResponse,
        responseCode: rentResponseCode,
        loading: rentLoading,
        fetcher: getPurchaseVod,
        reset: resetRent,
    } = useDataFetcher<any, FetchParamRent>(() => GSApi.purchaseVod(vodTermId, validatedPurchasePin), onNoInternet, onNetworkError);

    const { refreshVODPurchases } = useVODPurchases();
    const purchaseCompletedRef = useRef(false);
    const subscriptionCompletedRef = useRef(false);

    const initUpsellFlow = async (playerUrl: string, assetToUpsell: Episode | Movie | MovieDetails) => {
        const hasTvodTerms = assetToUpsell.vod_terms?.some((term) => term.type === 'TVOD');

        setPlayPath(playerUrl);
        setCheckingServices(true);

        try {
            const servicesResponse = await GSApi.getServices({ vod_asset_id: assetToUpsell.id, lang: appLanguage });
            const services = Array.isArray(servicesResponse?.response) ? servicesResponse.response : [];
            const hasServices = services.length > 0;

            setCheckingServices(false);

            // Determine which flow to use based on available options
            if (hasTvodTerms && hasServices) {
                // Both available - show upsell selector
                setAsset(assetToUpsell);
                setCachedServicesResponse(servicesResponse?.response);

                const tvodTerm = assetToUpsell.vod_terms?.find((term) => term.type === 'TVOD');
                if (tvodTerm) {
                    setVodTermId(tvodTerm.id);
                }

                setStep(UpsellFlowStep.SELECT_OFFER);
            } else if (hasTvodTerms && !hasServices) {
                // Only rent available - redirect to purchase flow
                initPurchaseFlow(playerUrl, assetToUpsell);
            } else if (!hasTvodTerms && hasServices) {
                // Only services available - redirect to subscription flow
                initSubscribeFlow(playerUrl, {
                    type: 'Product',
                    code: assetToUpsell.id,
                });
            }
        } catch {
            setCheckingServices(false);
            if (hasTvodTerms) {
                initPurchaseFlow(playerUrl, assetToUpsell);
            }
        }
    };

    const goToConfirmation = (flowStep: UpsellFlowStep) => {
        setStep(flowStep);
    };

    const goToEnterPin = () => {
        if (selectedOffer && !isRentable(selectedOffer)) {
            setPendingSubscriptionOffer(selectedOffer as SubscribeOption);
        }
        setStep(UpsellFlowStep.ENTER_PIN);
    };

    const exitFlow = () => {
        setAsset(null);
        setSelectedOffer(null);
        setSelectedRentOffer(null);
        setPendingSubscriptionOffer(null);
        setStep(null);
        setVodTermId(null);
        setPlayPath(null);
        setPurchaseErrorCode(null);
        resetRent();
        setCachedServicesResponse(null);
        closePinDialog(false);
        purchaseCompletedRef.current = false;
        subscriptionCompletedRef.current = false;
    };

    const onPinValidated = () => {
        // PIN validation handled by useEffect watching validatedPurchasePin
    };

    const onPinDialogClosed = () => {
        exitFlow();
    };

    const onPurchaseSuccessful = () => {
        dispatch(setPurchaseSynced());
        exitFlow();
        navigate(playPath);
        confirmTransactionSuccess(() => {});
    };

    const selectOffer = (offer: Rentable | SubscribeOption) => {
        setSelectedOffer(offer);
    };

    const goBackToSelectOffer = () => {
        setSelectedOffer(null);
        setStep(UpsellFlowStep.SELECT_OFFER);
    };

    const selectPurchaseOffer = (offer: Offer) => {
        setSelectedRentOffer({
            offerId: offer.catalog_id,
            productId: offer.id,
        });
        if (offer.id) {
            setVodTermId(offer.id);
        }
        setStep(UpsellFlowStep.ENTER_PIN);
    };

    useEffect(() => {
        if (selectedOffer) {
            goToConfirmation(isRentable(selectedOffer) ? UpsellFlowStep.CONFIRM_PURCHASE_OFFER : UpsellFlowStep.CONFIRM_SUBSCRIPTION_OFFER);
        }
    }, [selectedOffer]);

    useEffect(() => {
        if (selectedRentOffer) {
            goToEnterPin();
        }
    }, [selectedRentOffer]);

    useEffect(() => {
        const status = parseInt(`${rentResponseCode}`, 10);
        const isSuccessStatus = status >= 200 && status < 300;
        if (isSuccessStatus && asset && !rentError && !purchaseErrorCode) {
            onPurchaseSuccessful();
        }
    }, [rentResponseCode, rentResponse, rentError, purchaseErrorCode]);

    useEffect(() => {
        if (step && rentError) {
            setStep(UpsellFlowStep.ERROR);
        }
    }, [rentError]);

    useEffect(() => {
        if (step && step === UpsellFlowStep.SELECT_OFFER) {
            setSelectedOffer(null);
        }

        if (step && (step === UpsellFlowStep.CONFIRM_PURCHASE_OFFER || step === UpsellFlowStep.CONFIRM_SUBSCRIPTION_OFFER)) {
            closePinDialog(false);
        }

        if (step && step === UpsellFlowStep.ENTER_PIN) {
            openPinDialog(PinFlowType.CHECK, PinType.PURCHASE, {
                onPinValidated,
                onDialogClosed: onPinDialogClosed,
                onBack: () => {
                    const prevStep =
                        selectedOffer && !isRentable(selectedOffer)
                            ? UpsellFlowStep.CONFIRM_SUBSCRIPTION_OFFER
                            : UpsellFlowStep.CONFIRM_PURCHASE_OFFER;
                    setStep(prevStep);
                },
                canGoBack: true,
            });
        }

        if (step === UpsellFlowStep.ERROR) {
            closePinDialog(false);
        }
    }, [step]);

    useEffect(() => {
        const processPurchase = async () => {
            if (validatedPurchasePin && selectedRentOffer && vodTermId && !purchaseCompletedRef.current) {
                const purchaseResult = await getPurchaseVod({
                    vodTermId,
                    purchasePin: validatedPurchasePin,
                });

                purchaseCompletedRef.current = true;

                const errorCode = getPurchaseErrorCode(purchaseResult);
                if (errorCode) {
                    setPurchaseErrorCode(errorCode);
                    setStep(UpsellFlowStep.ERROR);
                    return;
                }

                await refreshVODPurchases(asset?.id);
            }
        };

        processPurchase();
    }, [validatedPurchasePin, selectedRentOffer, vodTermId]);

    useEffect(() => {
        const processSubscription = async () => {
            if (validatedPurchasePin && pendingSubscriptionOffer && !subscriptionCompletedRef.current) {
                try {
                    await purchaseSubscription(pendingSubscriptionOffer, validatedPurchasePin);
                    subscriptionCompletedRef.current = true;

                    dispatch(setPurchaseSynced());
                    exitFlow();
                    confirmTransactionSuccess(() => {});
                } catch {
                    setStep(UpsellFlowStep.ERROR);
                }
            }
        };

        processSubscription();
    }, [validatedPurchasePin, pendingSubscriptionOffer]);

    useEffect(
        () => () => {
            purchaseCompletedRef.current = false;
        },
        [validatedPurchasePin, selectedRentOffer, vodTermId]
    );

    useEffect(
        () => () => {
            subscriptionCompletedRef.current = false;
        },
        [validatedPurchasePin, pendingSubscriptionOffer]
    );

    return {
        pendingFlow: asset != null,
        step,
        asset,
        selectedOffer,
        checkingServices,

        cachedServicesResponse,
        rentError,
        rentLoading,
        purchaseErrorCode,

        initUpsellFlow,
        cancel: exitFlow,
        selectOffer,
        selectPurchaseOffer,
        goToEnterPin,
        goBackToSelectOffer,
    };
};

export const UpsellFlowContext = createContext<ReturnType<typeof useUpsellFlowService>>(null);

export const UpsellFlowProvider = ({ children }) => {
    const service = useUpsellFlowService();
    return <UpsellFlowContext.Provider value={service}>{children}</UpsellFlowContext.Provider>;
};

export const useUpsellFlow = () => useContext(UpsellFlowContext);

export const useUpsellFlowScreenProvider = () => {
    const {
        pendingFlow,
        asset,
        step,
        cachedServicesResponse,
        selectedOffer,
        rentError,
        purchaseErrorCode,
        cancel,
        selectOffer,
        selectPurchaseOffer,
        goToEnterPin,
        goBackToSelectOffer,
    } = useUpsellFlow();

    const getSelectOfferScreen = () => {
        const vodTerms = asset?.vod_terms ?? [];
        const tvodTerms = vodTerms.filter((term) => term.type === 'TVOD');

        if (step && !cachedServicesResponse) {
            return <LoadingIndicator overlay={true} />;
        }

        const options: ServiceOptionCover[] = [];

        if (tvodTerms.length > 0) {
            const tvodTerm = tvodTerms[0];
            const watchPeriodHours = tvodTerm.watch_period ? Math.round(tvodTerm.watch_period / 3600) : 48;

            options.push({
                id: tvodTerm.id,
                image: asset?.image,
                duration: `${watchPeriodHours}h`,
                price: tvodTerm.price,
                currency: tvodTerm.currency,
                title: asset.title,
                optionTypeLabel: translate(isEpisode(asset) ? 'TYPE_EPISODE' : 'TYPE_MOVIE'),
                type: isEpisode(asset) ? 'EPISODE' : 'MOVIE',
                rawData: null,
                rentableData: asset,
                onClick: () => {
                    selectOffer({
                        ...asset,
                        rentOffers: asset.rentOffers?.length ? asset.rentOffers : [{ offerValue: tvodTerm.price } as any],
                        buyOffers: [],
                    });
                },
            });
        }

        if (cachedServicesResponse) {
            const services: Service[] = Array.isArray(cachedServicesResponse) ? cachedServicesResponse : [];

            services.forEach((offer) => {
                const price = offer.price.value;
                const productId = offer.id;

                options.push({
                    id: `${productId}`,
                    title: offer.name,
                    price,
                    currency: 'EUR',
                    optionTypeLabel: getOfferTypeByTag(offer.type),
                    type: 'SUBSCRIPTION',
                    image: offer.image_by_type?.poster || asset?.image,
                    duration: translate('SUBSCRIPTION_PERIOD_MONTH'),
                    rawData: {
                        name: offer.name,
                        tags: [offer.type],
                        price,
                        productId,
                        poster: offer.image_by_type?.poster || '',
                        description: offer.description,
                    },
                    onClick: () => {
                        selectOffer({
                            name: offer.name,
                            tags: [offer.type],
                            price,
                            productId,
                            poster: offer.image_by_type?.poster || '',
                            description: offer.description,
                            assetId: asset?.id,
                            packageId: `${offer.id}`,
                        });
                    },
                });
            });
        }

        return <UpsellOptionSelector onClose={cancel} options={options} onBack={cancel} />;
    };

    const getConfirmSubscriptionOfferScreen = () => (
        <SubscriptionConfirmation
            onClose={cancel}
            onBack={goBackToSelectOffer}
            onConfirm={goToEnterPin}
            selectedOffer={selectedOffer as SubscribeOption}
            loading={null}
        />
    );

    const getErrorAlert = () => {
        if (purchaseErrorCode) {
            const { titleKey, bodyKey, isHtmlBody } = getPurchaseErrorTranslationKeys(purchaseErrorCode);
            const bodyText = isHtmlBody ? <span dangerouslySetInnerHTML={{ __html: translate(bodyKey) }} /> : translate(bodyKey);

            return (
                <AlertDialog
                    title={translate(titleKey)}
                    bodyText={bodyText}
                    buttons={[
                        {
                            text: translate('OK_BUTTON'),
                            onClick: cancel,
                        },
                    ]}
                />
            );
        }

        if (rentError) {
            const code = parseInt(rentError?.response?.status, 10);

            return (
                <AlertDialog
                    title={translate('ERR_RENTAL_GENERIC_TITLE')}
                    bodyText={translate('ERR_RENTAL_GENERIC_BODY')}
                    hintText={`${translate('ERR_CODE')} ${code}`}
                    buttons={[
                        {
                            text: translate('OK_BUTTON'),
                            onClick: cancel,
                        },
                    ]}
                />
            );
        }

        return null;
    };

    const getScreen = () => {
        if (!step) {
            return null;
        }

        if (pendingFlow) {
            if (step === UpsellFlowStep.SELECT_OFFER) {
                return getSelectOfferScreen();
            }

            if (step === UpsellFlowStep.CONFIRM_PURCHASE_OFFER) {
                return (
                    <PurchaseOptionConfirmation
                        assetOverride={asset}
                        cancelOverride={cancel}
                        selectPurchaseOfferOverride={selectPurchaseOffer}
                    />
                );
            }

            if (step === UpsellFlowStep.CONFIRM_SUBSCRIPTION_OFFER) {
                return getConfirmSubscriptionOfferScreen();
            }

            if (step === UpsellFlowStep.ENTER_PIN) {
                return selectedOffer && !isRentable(selectedOffer) ? (
                    getConfirmSubscriptionOfferScreen()
                ) : (
                    <PurchaseOptionConfirmation
                        assetOverride={asset}
                        cancelOverride={cancel}
                        selectPurchaseOfferOverride={selectPurchaseOffer}
                    />
                );
            }
        }

        return getErrorAlert();
    };

    return getScreen;
};
